import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, createCheckout, waLink } from "@/lib/api";

let paypalScriptPromise = null;
const loadPaypalSdk = (clientId) => {
  if (!paypalScriptPromise) {
    paypalScriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=EUR&locale=it_IT`;
      s.onload = resolve;
      s.onerror = reject;
      document.body.appendChild(s);
    });
  }
  return paypalScriptPromise;
};

export const CheckoutModal = ({ pkg, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", problem: "" });
  const [loading, setLoading] = useState(false);
  const [paidData, setPaidData] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalRef = useRef(null);
  const buttonsRendered = useRef(false);
  const formRef = useRef(form);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const formValid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.phone.trim();

  useEffect(() => {
    if (!pkg) return;
    let cancelled = false;
    axios.get(`${API}/payments/paypal/config`)
      .then(({ data }) => loadPaypalSdk(data.client_id))
      .then(() => { if (!cancelled) setPaypalReady(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pkg]);

  useEffect(() => {
    if (!paypalReady || !paypalRef.current || buttonsRendered.current || !window.paypal) return;
    buttonsRendered.current = true;
    window.paypal.Buttons({
      style: { layout: "vertical", color: "gold", shape: "rect", label: "paypal", height: 48, tagline: false },
      createOrder: async () => {
        const f = formRef.current;
        const valid = f.name.trim() && /\S+@\S+\.\S+/.test(f.email) && f.phone.trim();
        if (!valid) {
          toast.error("Compila prima nome, email e telefono.");
          throw new Error("form invalid");
        }
        const { data } = await axios.post(`${API}/payments/paypal/create`, {
          package_id: pkg.id, ...f,
        });
        return data.order_id;
      },
      onApprove: async (data) => {
        try {
          const res = await axios.post(`${API}/payments/paypal/capture/${data.orderID}`);
          setPaidData(res.data);
        } catch {
          toast.error("Pagamento non completato. Riprova o usa la carta.");
        }
      },
      onError: () => toast.error("Errore PayPal. Riprova o usa la carta."),
    }).render(paypalRef.current);
  }, [paypalReady, pkg, form, formValid]);

  const submitStripe = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        package_id: pkg.id,
        origin_url: window.location.origin,
        ...form,
      });
      window.location.href = checkout_url;
    } catch {
      toast.error("Errore durante la creazione del pagamento. Riprova.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {pkg && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-mossdeep/70 backdrop-blur-sm p-0 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            data-testid="checkout-modal"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-t-[2rem] sm:rounded-[2rem] bg-alabaster p-8 max-h-[92vh] overflow-y-auto"
          >
            {paidData ? (
              <div className="text-center" data-testid="paypal-success-panel">
                <CheckCircle2 size={48} className="mx-auto text-mosslight" />
                <h3 className="mt-4 font-serif text-3xl font-semibold text-moss">
                  Pagamento riuscito{paidData.customer_name ? `, ${paidData.customer_name.split(" ")[0]}` : ""}!
                </h3>
                <p className="mt-1 text-sm font-bold uppercase tracking-widest text-terracotta">{paidData.tier}</p>
                <div className="mt-6 rounded-2xl bg-tint border-2 border-dashed border-gold/60 px-6 py-5">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-sub font-bold">Il tuo Voucher</p>
                  <p className="mt-1.5 font-serif text-3xl font-bold tracking-wide text-terracotta" data-testid="paypal-credit-code">
                    {paidData.credit_code}
                  </p>
                </div>
                <p className="mt-4 text-xs text-sub">
                  Ti abbiamo inviato il voucher anche via email.{" "}
                  {paidData.available_now
                    ? "Il tuo voucher è attivo adesso."
                    : `Si attiva ${paidData.schedule_label}, ma puoi già scrivermi per organizzarci.`}
                </p>
                <a
                  href={waLink(`${(paidData.tier || "Voucher").toUpperCase()} — Codice ${paidData.credit_code}. Buongiorno Dott. Vincenti, ho appena pagato con PayPal e le invio le foto del problema.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="paypal-whatsapp-link"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-moss py-4 text-base font-semibold text-alabaster transition-all duration-200 hover:bg-mosslight"
                >
                  <MessageCircle size={18} />
                  Avvia chat WhatsApp col Dottore
                </a>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta font-bold">
                      Checkout sicuro
                    </p>
                    <h3 className="mt-2 font-serif text-2xl font-semibold text-moss">{pkg.name}</h3>
                    <p className="mt-1 text-sm text-sub">
                      {pkg.tier} · {pkg.priceNet} + IVA ({pkg.priceGross} ivato)
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    data-testid="checkout-close-button"
                    className="rounded-full p-2 text-sub hover:bg-tint transition-colors"
                    aria-label="Chiudi"
                  >
                    <X size={20} />
                  </button>
                </div>

                <form data-testid="checkout-submit-form" onSubmit={submitStripe} className="mt-7 space-y-4">
                  <div>
                    <label htmlFor="co-name" className="block text-xs font-semibold text-sub mb-1.5">
                      Nome e Cognome *
                    </label>
                    <input
                      id="co-name"
                      data-testid="checkout-name-input"
                      required
                      value={form.name}
                      onChange={set("name")}
                      className="w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss/40"
                      placeholder="Mario Rossi"
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="co-email" className="block text-xs font-semibold text-sub mb-1.5">
                        Email *
                      </label>
                      <input
                        id="co-email"
                        data-testid="checkout-email-input"
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        className="w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss/40"
                        placeholder="mario@email.it"
                      />
                    </div>
                    <div>
                      <label htmlFor="co-phone" className="block text-xs font-semibold text-sub mb-1.5">
                        Telefono *
                      </label>
                      <input
                        id="co-phone"
                        data-testid="checkout-phone-input"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={set("phone")}
                        className="w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss/40"
                        placeholder="+39 333 0000000"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="co-problem" className="block text-xs font-semibold text-sub mb-1.5">
                      Quale problema hai? (facoltativo)
                    </label>
                    <textarea
                      id="co-problem"
                      data-testid="checkout-problem-input"
                      rows={2}
                      value={form.problem}
                      onChange={set("problem")}
                      className="w-full rounded-2xl border border-line bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-moss/40 resize-none"
                      placeholder="Es. scarafaggi in cucina da una settimana..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="checkout-stripe-btn"
                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-2xl bg-moss py-4 text-lg font-bold text-alabaster transition-all duration-200 hover:bg-mosslight disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={17} className="shrink-0" />}
                    {loading ? "Reindirizzamento..." : `Paga ${pkg.priceGross}`}
                  </button>
                  <p className="text-center text-xs text-sub -mt-1">
                    Pagamento sicuro Stripe · Carta, Apple Pay, Google Pay
                  </p>
                  <div className="flex items-center justify-center gap-2.5" data-testid="payment-logos">
                    <img src="/pay-logos/visa.svg" alt="Visa" className="h-6 w-auto rounded-[4px]" />
                    <img src="/pay-logos/mastercard.svg" alt="Mastercard" className="h-6 w-auto rounded-[4px]" />
                    <img src="/pay-logos/apple_pay.svg" alt="Apple Pay" className="h-6 w-auto rounded-[4px]" />
                    <img src="/pay-logos/google_pay.svg" alt="Google Pay" className="h-6 w-auto rounded-[4px]" />
                  </div>

                  <div className="flex items-center gap-3 text-xs text-sub">
                    <span className="h-px flex-1 bg-line" />
                    oppure
                    <span className="h-px flex-1 bg-line" />
                  </div>

                  <div data-testid="checkout-paypal-btn">
                    <div ref={paypalRef} className={formValid ? "" : "opacity-50 pointer-events-none"} />
                    {!formValid && (
                      <p className="text-center text-[11px] text-sub mt-1">
                        Compila nome, email e telefono per pagare con PayPal
                      </p>
                    )}
                  </div>

                  <p className="text-center text-[11px] leading-relaxed text-sub">
                    Pagamenti cifrati gestiti da Stripe e PayPal. Dopo il pagamento ricevi subito il tuo
                    Voucher (anche via email) e il contatto WhatsApp diretto del Dott. Vincenti.
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
